import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/orgApi";
import { isValidAppEmail } from "@/lib/appEmail";
import { USR } from "@/constants/testIds";

const ROLES = [
  ["administrator", "Administrator"], ["front_desk", "Front Desk"],
  ["medical_assistant", "Medical Assistant"], ["nurse", "Nurse"],
  ["np", "NP"], ["pa", "PA"], ["md_do", "MD/DO"], ["biller", "Biller"],
];

const BLANK = {
  username: "", email: "", firstName: "", lastName: "", title: "", phone: "",
  password: "", active: true, orgWide: false, roles: [], orgId: "",
  locationIds: [], defaultLoc: "",
};

export default function UserForm({ open, onClose, editing, onSaved }) {
  const [f, setF] = useState(BLANK);
  const [orgs, setOrgs] = useState([]);
  const [locs, setLocs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setShowPassword(false);
    Promise.all([api.get("/organizations"), api.get("/locations")]).then(([o, l]) => {
      setOrgs(o.data.items || []);
      setLocs(l.data.items || []);
      if (editing) {
        setF({
          username: editing.username || "", email: editing.email || "",
          firstName: editing.firstName || "", lastName: editing.lastName || "",
          title: editing.title || "", phone: editing.phone || "", password: "",
          active: editing.active !== false, orgWide: !!editing.orgWide,
          roles: editing.roles || [],
          orgId: (editing.primaryOrganization?.reference || "").split("/")[1] || "",
          locationIds: (editing.locationAccess || []).map((la) => (la.location?.reference || "").split("/")[1]),
          defaultLoc: (editing.locationAccess || []).find((la) => la.isDefault)?.location?.reference?.split("/")[1] || "",
        });
      } else {
        const base = { ...BLANK };
        if ((o.data.items || []).length === 1) base.orgId = o.data.items[0].id;
        setF(base);
      }
    });
  }, [open, editing]);

  const toggleRole = (code) => setF((p) => ({
    ...p, roles: p.roles.includes(code) ? p.roles.filter((r) => r !== code) : [...p.roles, code],
  }));
  const toggleLoc = (id) => setF((p) => ({
    ...p, locationIds: p.locationIds.includes(id) ? p.locationIds.filter((x) => x !== id) : [...p.locationIds, id],
  }));

  const submit = async () => {
    if (!f.username.trim() || !f.email.trim()) return toast.error("Username and email are required.");
    if (!isValidAppEmail(f.email)) return toast.error("Enter a valid email address.");
    if (!f.firstName.trim() || !f.lastName.trim()) return toast.error("First and last name are required.");
    if (!f.roles.length) return toast.error("Select at least one role.");
    if (!f.orgId) return toast.error("A primary organization is required.");
    if (!editing && f.password.length < 8) return toast.error("Password must be at least 8 characters.");

    const org = orgs.find((o) => o.id === f.orgId);
    const orgRef = { reference: `Organization/${f.orgId}`, display: org?.name };
    const locationAccess = f.locationIds.map((id) => {
      const l = locs.find((x) => x.id === id);
      return {
        location: { reference: `Location/${id}`, display: l?.name },
        organization: orgRef,
        isDefault: id === f.defaultLoc,
      };
    });
    const payload = {
      username: f.username.trim(), email: f.email.trim(),
      firstName: f.firstName.trim(), lastName: f.lastName.trim(),
      middleName: null, title: f.title.trim() || null, phone: f.phone.trim() || null,
      active: f.active, orgWide: f.orgWide, roles: f.roles,
      primaryOrganization: orgRef, organizations: [orgRef], locationAccess,
      practitioner: null,
      ...(f.password ? { password: f.password } : {}),
    };
    setSaving(true);
    try {
      if (editing?.id) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "User updated." : "User created.");
      onSaved(); onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map((x) => x.msg).join("; ") : d || "Failed to save user.");
    } finally { setSaving(false); }
  };

  const orgLocs = locs; // single-org launch: show all locations

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid={USR.form}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit User" : "New User"}</DialogTitle>
          <DialogDescription>Application account — separate from FHIR Practitioner</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 gap-3 py-2 sm:grid-cols-2">
          <div className="space-y-1.5"><Label>Username *</Label><Input data-testid={USR.fUsername} value={f.username} onChange={(e) => set("username")(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Email *</Label><Input data-testid={USR.fEmail} value={f.email} onChange={(e) => set("email")(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>First name *</Label><Input data-testid={USR.fFirst} value={f.firstName} onChange={(e) => set("firstName")(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Last name *</Label><Input data-testid={USR.fLast} value={f.lastName} onChange={(e) => set("lastName")(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Title (e.g. MD, NP)</Label><Input data-testid={USR.fTitle} value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="MD" /></div>
          <div className="space-y-1.5"><Label>Phone</Label><Input data-testid={USR.fPhone} value={f.phone} onChange={(e) => set("phone")(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>{editing ? "Reset password (optional)" : "Password *"}</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                data-testid={USR.fPassword}
                value={f.password}
                autoComplete="new-password"
                onChange={(e) => set("password")(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                data-testid={USR.fPasswordToggle}
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-slate-400 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-600/40"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Primary organization *</Label>
            <Select value={f.orgId} onValueChange={set("orgId")}>
              <SelectTrigger data-testid={USR.fOrg}><SelectValue placeholder="Select organization" /></SelectTrigger>
              <SelectContent>{orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label className="text-xs font-semibold uppercase tracking-widest text-teal-700">Roles *</Label>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {ROLES.map(([code, label]) => (
              <label key={code} className="flex items-center gap-2 text-sm">
                <Checkbox checked={f.roles.includes(code)} onCheckedChange={() => toggleRole(code)}
                  data-testid={`${USR.roleOption}-${code}`} />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.active} onCheckedChange={set("active")} data-testid={USR.fActive} /> Active
            </label>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={f.orgWide} onCheckedChange={set("orgWide")} data-testid={USR.fOrgWide} /> Organization-wide access
            </label>
          </div>
          {!f.orgWide && (
            <>
              <Label className="text-xs font-semibold uppercase tracking-widest text-teal-700">Location access</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {orgLocs.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={f.locationIds.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)}
                      data-testid={`${USR.locOption}-${l.id}`} />
                    {l.name}
                  </label>
                ))}
              </div>
              {f.locationIds.length > 0 && (
                <div className="space-y-1.5">
                  <Label>Default location</Label>
                  <Select value={f.defaultLoc} onValueChange={set("defaultLoc")}>
                    <SelectTrigger data-testid={USR.fDefaultLoc}><SelectValue placeholder="Select default" /></SelectTrigger>
                    <SelectContent>
                      {f.locationIds.map((id) => {
                        const l = locs.find((x) => x.id === id);
                        return <SelectItem key={id} value={id}>{l?.name}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={onClose} data-testid={USR.cancelBtn}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid={USR.saveBtn} className="bg-teal-700 hover:bg-teal-800">
            {saving ? "Saving…" : editing ? "Save changes" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
